import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoUrl from '@/assets/LogoForni.png';
import footerUrl from '@/assets/ContactoForni.jpg';
import { currencyFromSp, formatMoney, parseMoney } from '@/shared/lib/money';
import { formatIvaRate } from '@/shared/countries';
import type { ResumenOperacionesItem } from '@/shared/api/controllers/resumenOperaciones';
import type { DetalleOperacionesItem } from '@/shared/api/controllers/detalleOperaciones';
import type { ContactoItem } from '@/shared/api/controllers/contactos';
import type { ArticuloItem } from '@/shared/api/controllers/articulos';
import { calcLineTotals, calcInvoiceTotals, type IvaRate } from '../lib/calc';
import { esClienteExterior } from '../lib/ivaExterior';

/**
 * Cotización PDF — mirrors the legacy PowerApps PDF (Screen_DetalleVenta
 * `HTML_PDF_Ver3_DV`). Red branded header strip, line table with the
 * REFERENCIA / DESCRIPCIÓN / CANTIDAD / PRECIO UNITARIO / % DESC /
 * DESCUENTO / TOTAL columns, SUBTOTAL/IVA/TOTAL summary rows, and the
 * standard Términos y condiciones block at the bottom.
 *
 * No "FOTO" column — the legacy PDF embedded thumbnails by article, but
 * we don't carry per-line photo data on `02.DetalleOperaciones`. The
 * column is dropped to keep the table readable rather than padded with
 * placeholders.
 */

export type CotizacionPdfInput = {
  header: ResumenOperacionesItem;
  lines: DetalleOperacionesItem[];
  contacto: ContactoItem | null;
  articulos: ArticuloItem[];
  /**
   * Optional articulo-id → image data URL map. The caller pre-fetches
   * images via `fetchArticuloImageBlobUrl` (or similar) and converts
   * them to base64 because jsPDF's `addImage` requires a synchronous
   * data URL, not a blob URL or remote URL.
   */
  imagesByArticuloId?: Map<string, string>;
  /**
   * Active country at generation time. Drives the footer block (AR keeps
   * the legacy `ContactoForni.jpg` banner; MX renders an FN FORNITALIA
   * MX text block with the Mexican fiscal address) and the terms list
   * (MX gets a shorter Mexican-market version). Defaults to AR for the
   * caller-doesn't-care case so the legacy behaviour is unchanged.
   */
  country?: 'AR' | 'MX';
};

const BRAND_RED: [number, number, number] = [206, 44, 55];

export async function generateCotizacionPDF(
  input: CotizacionPdfInput,
): Promise<Blob> {
  const { header, lines, contacto, articulos, imagesByArticuloId } = input;
  const country = input.country ?? 'AR';
  const images = imagesByArticuloId ?? new Map<string, string>();
  const fields = header.fields;
  // Honor MXN (and the legacy 'Dolares' / 'Pesos Mexicanos' SP labels)
  // alongside the 3-letter codes. The previous AR-or-USD-only mapping
  // silently rendered MX cotizaciones as ARS.
  const moneda = currencyFromSp(fields.Moneda_RO);

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;

  // --- header: logo + COTIZACIÓN N° + cliente ---
  const logoDataUrl = await loadImageAsDataUrl(logoUrl);
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', margin, margin, 40, 16);
  }

  let y = margin + 24;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(`COTIZACIÓN N° ${fields.NrCotizacion_RO ?? header.id}`, margin, y);

  // "Versión actualizada al dd/mm/yyyy" stamp — only when the venta
  // has been edited after creation. SP's lastModifiedDateTime ticks
  // on every PATCH; a 60-second tolerance against createdDateTime
  // skips the trivial post-creation NrCotizacion_RO write so a fresh
  // cotización doesn't get stamped on its first download.
  // Right-aligned next to the cotización number for visibility, in
  // brand red so the customer notices the version when comparing
  // against an earlier copy they may have already received.
  const created = new Date(header.createdDateTime).getTime();
  const modified = new Date(header.lastModifiedDateTime).getTime();
  const wasEdited =
    Number.isFinite(created) &&
    Number.isFinite(modified) &&
    modified - created > 60_000;
  if (wasEdited) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_RED);
    const stamp = `Versión actualizada al ${formatDateAr(header.lastModifiedDateTime)}`;
    const stampW = doc.getTextWidth(stamp);
    doc.text(stamp, pageWidth - margin - stampW, y);
  }

  y += 7;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const clienteName =
    contacto?.fields.NombreApellido_C?.trim() ||
    fields.Cliente_RO ||
    '—';
  doc.text('Cliente: ', margin, y);
  doc.setFont('helvetica', 'normal');
  const clienteLabelW = doc.getTextWidth('Cliente: ');
  doc.text(clienteName, margin + clienteLabelW, y);
  y += 6;
  doc.setFontSize(10);
  doc.text(
    'Nos complace poner a su consideración la siguiente cotización:',
    margin,
    y,
  );
  y += 4;

  // --- line items table ---
  // Index articles by id for fast description lookup.
  const articuloById = new Map<string, ArticuloItem>();
  for (const a of articulos) {
    articuloById.set(a.id, a);
  }

  const rows = lines
    .filter((l) => (l.fields.Status_DO ?? 'Activo') !== 'Anulado')
    .map((l) => {
      // `IvaRate` is `number` — accept any positive parsed rate (AR 21 /
      // 10.5, MX 16 / 8, future rates) rather than coercing non-AR values
      // to 0. Exento ('0' or 'IVA Exento') still yields 0.
      const ivaRaw = (l.fields.IVA_RO ?? '0').toString().replace(',', '.');
      const ivaNum = Number(ivaRaw);
      const iva: IvaRate = Number.isFinite(ivaNum) && ivaNum > 0 ? ivaNum : 0;
      const monto = parseMoney(l.fields.Monto_RO) ?? 0;
      const cantidad = parseMoney(l.fields.Cantidad_RO) ?? 0;
      const descuento = parseMoney(l.fields.Descuento_RO) ?? 0;
      const t = calcLineTotals({
        monto,
        cantidad,
        descuento,
        iva,
        esRegalo: (l.fields.EsRegalo_DO ?? 'NO') === 'SI',
      });
      const articuloId = l.fields.IDArticulo_DO ?? '';
      const articulo = articuloId ? articuloById.get(articuloId) : null;
      // Garantía de la línea (lib/garantias.ts del server): meses desde la
      // entrega, o la fecha manual si el producto no tiene duración.
      const vence = l.fields.GarantiaVence_DO;
      const garantia =
        l.fields.LlevaGarantia_DO !== 'SI'
          ? ''
          : l.fields.GarantiaManual_DO === 'SI' && vence
            ? `Garantía hasta el ${vence.slice(8, 10)}/${vence.slice(5, 7)}/${vence.slice(0, 4)}`
            : `Garantía: ${l.fields.GarantiaMeses_DO} meses desde la entrega`;
      // Ingreso estimado de las líneas que dependen de stock por ingresar
      // (informativo: lo elige el vendedor en la línea).
      const ingreso = l.fields.FechaEstimadaIngreso_DO?.trim();
      const ingresoEstimado =
        ingreso && /^\d{4}-\d{2}-\d{2}/.test(ingreso)
          ? `Ingreso estimado: ${ingreso.slice(8, 10)}/${ingreso.slice(5, 7)}/${ingreso.slice(0, 4)}`
          : '';
      const descripcion = [articulo?.fields.Descripcion_ART?.trim(), garantia, ingresoEstimado]
        .filter(Boolean)
        .join('\n');
      const referencia = l.fields.Articulo_DO ?? articulo?.fields.Articulo_ART ?? '—';
      const descuentoMonto = t.subtotal - t.subtotalDescontado;
      return {
        articuloId,
        referencia,
        descripcion,
        cantidad,
        monto,
        descuentoPct: descuento,
        descuentoMonto,
        total: t.subtotalDescontado,
      };
    });

  const FOTO_COL_INDEX = 1; // REFERENCIA, FOTO, DESCRIPCIÓN, …
  const FOTO_CELL_HEIGHT = 22; // mm — tall enough to fit a square thumbnail

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    // Una fila no se parte entre páginas: si no entra, pasa entera a la
    // siguiente (antes quedaba un encabezado con una franja vacía de la fila
    // cortada y la foto partida al medio).
    rowPageBreak: 'avoid',
    styles: {
      fontSize: 8,
      font: 'helvetica',
      cellPadding: 2,
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      overflow: 'linebreak',
      valign: 'top',
    },
    headStyles: {
      fillColor: BRAND_RED,
      textColor: 255,
      fontStyle: 'bold',
      halign: 'left',
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
    },
    head: [
      [
        'REFERENCIA',
        'FOTO',
        'DESCRIPCIÓN',
        'CANT.',
        'PRECIO UNIT.',
        '% DESC.',
        'DESCUENTO',
        'TOTAL',
      ],
    ],
    columnStyles: {
      0: { cellWidth: 26 },
      1: { cellWidth: 24, halign: 'center', valign: 'middle', minCellHeight: FOTO_CELL_HEIGHT },
      2: { cellWidth: 'auto' },
      3: { cellWidth: 12, halign: 'center' },
      // Precio y total con lugar para "$ 6.999.000,00" sin partirse en dos renglones.
      4: { cellWidth: 25, halign: 'right' },
      5: { cellWidth: 14, halign: 'right' },
      6: { cellWidth: 22, halign: 'right' },
      7: { cellWidth: 26, halign: 'right' },
    },
    body: rows.map((r) => [
      r.referencia,
      '', // FOTO — drawn via didDrawCell hook
      r.descripcion,
      formatQty(r.cantidad),
      formatMoney(r.monto, moneda),
      formatPct(r.descuentoPct),
      r.descuentoMonto > 0 ? formatMoney(r.descuentoMonto, moneda) : '$ -',
      formatMoney(r.total, moneda),
    ]),
    didDrawCell: (data) => {
      if (data.section !== 'body') return;
      if (data.column.index !== FOTO_COL_INDEX) return;
      const row = rows[data.row.index];
      if (!row) return;
      const cell = data.cell;
      const pad = 1;
      const maxW = cell.width - pad * 2;
      const maxH = cell.height - pad * 2;
      const side = Math.min(maxW, maxH);
      const x = cell.x + (cell.width - side) / 2;
      const y = cell.y + (cell.height - side) / 2;
      const dataUrl = images.get(row.articuloId);
      const format = dataUrl ? detectImageFormat(dataUrl) : null;
      if (dataUrl && format) {
        try {
          doc.addImage(dataUrl, format, x, y, side, side);
          return;
        } catch {
          // jsPDF throws on unsupported formats — fall through to the
          // placeholder rather than corrupt the PDF.
        }
      }
      drawNoImagePlaceholder(doc, x, y, side);
    },
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;

  // --- subtotal / iva / total summary ---
  const totals = calcInvoiceTotals(
    lines
      .filter((l) => (l.fields.Status_DO ?? 'Activo') !== 'Anulado')
      .map((l) => {
        const ivaRaw = (l.fields.IVA_RO ?? '0').toString().replace(',', '.');
        const ivaNum = Number(ivaRaw);
        const iva: IvaRate = Number.isFinite(ivaNum) && ivaNum > 0 ? ivaNum : 0;
        return {
          monto: parseMoney(l.fields.Monto_RO) ?? 0,
          cantidad: parseMoney(l.fields.Cantidad_RO) ?? 0,
          descuento: parseMoney(l.fields.Descuento_RO) ?? 0,
          iva,
          esRegalo: (l.fields.EsRegalo_DO ?? 'NO') === 'SI',
        };
      }),
  );
  // Sum across all rate buckets — `ivaByRate` is country-agnostic, so
  // MX 16/8 land here too. The legacy `iva21 + iva105` summation only
  // covered AR rates and produced $0 for any MX cotización.
  const ivaTotal = Object.values(totals.ivaByRate).reduce(
    (sum, v) => sum + v,
    0,
  );
  // Per-rate breakdown rows in the summary table. AR cotizaciones with
  // mixed 21 / 10.5 lines show two rows; MX shows IVA 16% (or 8%, etc.).
  // Sorted descending by rate for stable visual order.
  const ivaRows = Object.entries(totals.ivaByRate)
    .map(([k, v]) => [Number(k), v] as const)
    .filter(([rate, amount]) => rate > 0 && amount > 0)
    .sort((a, b) => b[0] - a[0])
    .map(([rate, amount]) => [
      `IVA ${formatIvaRate(rate)}%`,
      formatMoney(amount, moneda),
    ]);

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 9,
      font: 'helvetica',
      cellPadding: 2,
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
    },
    columnStyles: {
      0: {
        cellWidth: 30,
        fillColor: BRAND_RED,
        textColor: 255,
        fontStyle: 'bold',
      },
      1: { cellWidth: 'auto', halign: 'right' },
    },
    body: [
      ['SUBTOTAL', formatMoney(totals.subtotalDescontado, moneda)],
      // One row per non-exento IVA rate present in the cotización. Falls
      // back to a single "IVA" $0 row when every line is exento, to keep
      // the visual shape of the summary table consistent. Cliente del
      // exterior (exportación, Factura E): sin fila de IVA.
      ...(ivaRows.length > 0
        ? ivaRows
        : esClienteExterior(contacto?.fields.CP_C)
          ? []
          : [['IVA', formatMoney(ivaTotal, moneda)]]),
      [
        {
          content: 'TOTAL',
          styles: { fillColor: BRAND_RED, textColor: 255, fontStyle: 'bold' },
        },
        {
          content: formatMoney(totals.total, moneda),
          styles: { halign: 'right', fontStyle: 'bold' },
        },
      ],
    ],
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;

  // --- terms & conditions block ---
  // QA #7: matches the reference template — black bold header, three
  // line styles based on the source marker:
  //   '* xxx'        → plain paragraph (no marker), paragraph spacing
  //   '• xxx'        → indented disc bullet, tight inter-line spacing
  //   '-IMPORTANTE:' → red bold standalone paragraph
  // Plus a single soft-rule highlight: any "Tiempo de entrega" line
  // renders red regardless of its marker.
  if (y > pageHeight - 100) {
    doc.addPage();
    y = margin;
  }
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Términos y condiciones:', margin, y);
  y += 6;

  const bodyFontSize = 9;
  const lineHeight = 4.2;
  const paragraphGap = 2.4;
  const bulletGap = 0.8;
  const bulletIndent = 5; // mm — left padding before the disc marker
  const bulletTextOffset = 3; // mm — gap between marker and text

  doc.setFontSize(bodyFontSize);

  for (const raw of termsFor(country)) {
    const trimmed = raw.trim();
    let kind: 'paragraph' | 'bullet' | 'importante';
    let text: string;

    if (/^-?\s*IMPORTANTE\s*[:\-]/i.test(trimmed)) {
      kind = 'importante';
      text = trimmed.replace(/^-\s*/, '');
    } else if (trimmed.startsWith('•')) {
      kind = 'bullet';
      text = trimmed.replace(/^[•·]\s*/, '');
    } else if (trimmed.startsWith('*')) {
      kind = 'paragraph';
      text = trimmed.slice(1).trim();
    } else {
      kind = 'paragraph';
      text = trimmed;
    }

    const isTiempoEntrega = /tiempo de entrega/i.test(text);

    // Style per kind. IMPORTANTE wins on red+bold; Tiempo de entrega
    // is red but normal weight.
    if (kind === 'importante') {
      doc.setTextColor(...BRAND_RED);
      doc.setFont('helvetica', 'bold');
    } else if (isTiempoEntrega) {
      doc.setTextColor(...BRAND_RED);
      doc.setFont('helvetica', 'normal');
    } else {
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
    }

    if (kind === 'bullet') {
      const textX = margin + bulletIndent + bulletTextOffset;
      const wrapped = doc.splitTextToSize(text, pageWidth - margin - textX);
      if (y + wrapped.length * lineHeight > pageHeight - 18) {
        doc.addPage();
        y = margin;
      }
      doc.text('•', margin + bulletIndent, y);
      doc.text(wrapped, textX, y);
      y += wrapped.length * lineHeight + bulletGap;
    } else {
      const wrapped = doc.splitTextToSize(text, pageWidth - margin * 2);
      if (y + wrapped.length * lineHeight > pageHeight - 18) {
        doc.addPage();
        y = margin;
      }
      doc.text(wrapped, margin, y);
      y += wrapped.length * lineHeight + paragraphGap;
    }
  }

  // Reset for any downstream draws.
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');

  // --- footer (last page only) ---
  await drawFooter(doc, pageWidth, pageHeight, margin, country);

  return doc.output('blob');
}

export function cotizacionFileName(header: ResumenOperacionesItem): string {
  const nro = header.fields.NrCotizacion_RO || header.id;
  return `Cotizacion-${nro}.pdf`;
}

// ---------------------------------------------------------------------------

/**
 * Country-aware terms preamble. AR keeps the legacy list verbatim;
 * MX uses the shorter Mexican-market wording approved 2026-05-15 (no
 * local pickup, 5–10 día hábil shipping window, no FC A reference).
 * USD ventas on either country fall back to the country list — moneda
 * alone doesn't disambiguate jurisdiction.
 */
function termsFor(country: 'AR' | 'MX'): readonly string[] {
  return country === 'MX' ? TERMS_MX : TERMS_AR;
}

const TERMS_AR = [
  '* Todos los precios son en pesos Argentinos + IVA (10,5%). Para emisión de FC A deberá avisarlo previamente al pago compartiendo los datos fiscales. Aplican retenciones.',
  '* Los productos se retiran por nuestro local de Av. Elcano 3776, CABA • Es responsabilidad del cliente contar con personal para la carga y descarga de los equipos. Tener en cuenta el peso y volumen de los hornos en los modelos PROGAS 90, PROGAS 106, PROELECTRICO 90, HORNO GAS DE BANDA 50, HORNO ELECTRICO BANDA 100 Y HORNO DOBLE BANDA 200.',
  '• Precios sujetos a cambios sin previo aviso.',
  '• El precio establecido en la cotización es válido solo por 15 días después de la misma.',
  '* La cotización tiene que estar liquidada en su totalidad para poder entregar la mercancía.',
  // Recargo por cuotas (ABM Cuotas MercadoPago), pedido 2026-10-20.
  '* Los pagos con Mercado Pago en cuotas tienen un recargo sobre el monto facturado, según la cantidad de cuotas.',
  '• En caso de que el cliente pacte recoger el equipo directamente por nuestro local, el equipo se resguardará por 15 días después de su compra, posterior a este periodo, no nos hacemos responsables de la fecha de entrega, ni de los equipos almacenados.',
  '* La garantía del equipo es de 6 meses y está sujeta al manejo, y uso correcto del equipo. La garantía entra en vigencia con fecha de emisión de factura.',
  '-IMPORTANTE: La garantía para las referencias PROGAS 90, PROGAS 106, PROELECTRICO 90, HORNO GAS DE BANDA 50, HORNO ELECTRICO BANDA 100 Y HORNO DOBLE BANDA 200 debe ser previamente validada y autorizada por el servicio técnico (+52 55 2330 9952) sin excepción. Es sumamente importante la correcta instalación de estos equipos. Evite inconvenientes y consulte previamente a instalar.',
  '• La emisión de la factura debe ser dentro del mes de compra, no hay excepciones.',
  '• Cualquier daño ocasionado a la mercancía durante el envío es responsabilidad del cliente y del transporte.',
  '• Todo trámite relacionado con pérdida o siniestro del equipo durante su trayecto, el cliente tiene que verlo directamente con la compañía de transporte y sus seguros.',
  '• Al pagar o abonar esta cotización, se toman por aceptados todos los términos y condiciones de este documento.',
  '* El equipo debe ser revisado al momento de la recepción del mismo, si existe algún daño ocasionado por el transporte, contactar de inmediato al transporte para iniciar trámite del seguro.',
  '* En caso de cancelar el pedido después de pagado, aplicará un cobro del 10% sobre el valor total de la cotización.',
  '* Tiempo de entrega: Después del pago, el envío se efectuará a los 15 días hábiles.',
];

const TERMS_MX = [
  '* Los precios están en pesos mexicanos y son sujetos a cambios sin previo aviso.',
  '* El envío fuera de CDMX es adicional y corre a cargo del cliente. Las entregas en CDMX son a pie de calle, y el cliente debe contar con personal para la descarga.',
  '* Debe liquidarse en su totalidad antes de la entrega.',
  '* Si el cliente recoge el equipo, éste se resguardará por 15 días en el almacén.',
  '* La garantía es de 6 meses, sujeta al uso adecuado del equipo.',
  '* La factura debe emitirse en el mes de compra.',
  '* El cliente es responsable de los daños ocasionados durante el envío y debe gestionar cualquier siniestro con la compañía de transporte.',
  '* Al abonar la cotización, el cliente acepta los términos.',
  '* En caso de cancelación, se cobrará un 10% del valor total.',
  '* El envío se realiza en 5-10 días hábiles después del pago.',
];

/**
 * Country-aware footer.
 *   - AR: legacy branded banner image (`ContactoForni.jpg`) — marketing-
 *     team approved layout with CUIT / address / phone / site.
 *   - MX: text block with FN FORNITALIA's Mexican fiscal data. Drawn
 *     directly (no image asset) so changes are made in code.
 */
async function drawFooter(
  doc: jsPDF,
  pageWidth: number,
  pageHeight: number,
  margin: number,
  country: 'AR' | 'MX',
) {
  if (country === 'MX') {
    drawFooterMx(doc, pageWidth, pageHeight, margin);
    return;
  }
  const dataUrl = await loadImageAsDataUrl(footerUrl);
  if (!dataUrl) return;
  const props = doc.getImageProperties(dataUrl);
  const w = pageWidth - margin * 2;
  const h = (props.height / props.width) * w;
  const y = pageHeight - h - 4;
  doc.addImage(dataUrl, 'JPEG', margin, y, w, h);
}

/**
 * MX footer. Brand-red separator + business name, address, phone and
 * email rendered as text. Anchored to the bottom of the page like the
 * AR banner so the spacing reads the same in both variants.
 */
function drawFooterMx(
  doc: jsPDF,
  pageWidth: number,
  pageHeight: number,
  margin: number,
) {
  const lineHeight = 4;
  const lines = [
    { text: 'FN FORNITALIA', bold: true, size: 10 },
    {
      text:
        'LAGO MAYOR 116, 11320, ANAHUAC II SECCION, MIGUEL HIDALGO, CIUDAD DE MEXICO, CIUDAD DE MEXICO, México',
      bold: false,
      size: 8,
    },
    { text: '5539563525', bold: false, size: 8 },
    { text: 'mktfnfornitalia@gmail.com', bold: false, size: 8 },
  ];
  const blockHeight = lines.length * lineHeight + 2;
  const baseY = pageHeight - blockHeight - 4;

  // Brand-red separator (matches the strip at the top of the AR banner).
  doc.setDrawColor(...BRAND_RED);
  doc.setLineWidth(0.6);
  doc.line(margin, baseY - 2, pageWidth - margin, baseY - 2);

  doc.setTextColor(0, 0, 0);
  lines.forEach((line, i) => {
    doc.setFont('helvetica', line.bold ? 'bold' : 'normal');
    doc.setFontSize(line.size);
    doc.text(line.text, margin, baseY + (i + 1) * lineHeight);
  });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
}

/**
 * Render an ISO timestamp (or any Date-parseable string) as `dd/mm/yyyy`
 * for the AR audience. Falls back to the raw input on parse failure so
 * we don't blow up the PDF on an unexpected format.
 */
function formatDateAr(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function formatQty(n: number): string {
  if (Math.abs(n - Math.round(n)) < 0.01) return String(Math.round(n));
  return n.toFixed(2).replace('.', ',');
}

function formatPct(n: number): string {
  if (Math.abs(n - Math.round(n)) < 0.01) return String(Math.round(n));
  return n.toFixed(2).replace('.', ',');
}

function detectImageFormat(dataUrl: string): 'PNG' | 'JPEG' | 'WEBP' | null {
  // jsPDF only accepts these three formats. The data URL prefix tells us
  // which to pass. Anything else (gif, svg) → bail out.
  if (dataUrl.startsWith('data:image/png')) return 'PNG';
  if (dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')) return 'JPEG';
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
  return null;
}

/**
 * "Sin imagen" placeholder rendered into the FOTO cell when the article
 * has no associated photo. Approximates the lucide `Image` glyph using
 * jsPDF primitives (jsPDF can't render SVG): light gray frame, a
 * stylized mountain triangle, a small circle for the sun, and a
 * "Sin imagen" label below. `side` is the square size in mm.
 */
function drawNoImagePlaceholder(doc: jsPDF, x: number, y: number, side: number) {
  // Save the current color/style state so we don't bleed gray into the
  // table border that draws after this hook.
  const FRAME_GRAY: [number, number, number] = [200, 200, 200];
  const FILL_GRAY: [number, number, number] = [245, 245, 245];
  const ICON_GRAY: [number, number, number] = [160, 160, 160];

  // Background frame.
  doc.setDrawColor(...FRAME_GRAY);
  doc.setFillColor(...FILL_GRAY);
  doc.setLineWidth(0.2);
  doc.roundedRect(x, y, side, side, 0.8, 0.8, 'FD');

  // Icon area: top ~65% of the cell.
  const iconBox = {
    x: x + side * 0.2,
    y: y + side * 0.2,
    w: side * 0.6,
    h: side * 0.4,
  };

  doc.setDrawColor(...ICON_GRAY);
  doc.setFillColor(...ICON_GRAY);
  doc.setLineWidth(0.25);
  // Outer frame of the picture-icon.
  doc.rect(iconBox.x, iconBox.y, iconBox.w, iconBox.h, 'S');
  // "Sun" — small filled circle in the upper-left.
  const sunR = iconBox.h * 0.12;
  doc.circle(
    iconBox.x + iconBox.w * 0.25,
    iconBox.y + iconBox.h * 0.3,
    sunR,
    'F',
  );
  // "Mountain" — triangle filling the lower portion.
  const baseY = iconBox.y + iconBox.h - 0.4;
  doc.triangle(
    iconBox.x + iconBox.w * 0.15, baseY,
    iconBox.x + iconBox.w * 0.55, iconBox.y + iconBox.h * 0.45,
    iconBox.x + iconBox.w * 0.95, baseY,
    'F',
  );

  // Label below the icon.
  doc.setTextColor(...ICON_GRAY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.text('Sin imagen', x + side / 2, y + side - side * 0.12, { align: 'center' });

  // Reset to defaults the rest of the document expects.
  doc.setTextColor(0, 0, 0);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.1);
}

// Cache keyed by URL so logo + footer don't fight for the same slot
// (the prior single-slot cache returned the first-fetched image for
// every subsequent call).
const imageCache = new Map<string, string>();
async function loadImageAsDataUrl(url: string): Promise<string | null> {
  const cached = imageCache.get(url);
  if (cached) return cached;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    imageCache.set(url, dataUrl);
    return dataUrl;
  } catch {
    return null;
  }
}
